import type { ReactNode } from 'react';
import { CalendarClock, ClipboardList, MapPin, Phone, StickyNote, Truck } from 'lucide-react';
import {
  DISPATCH_CARD_EMPTY,
  DISPATCH_CARD_SECTION_TITLES,
  DISPATCH_CARD_TITLE,
  joinParts,
} from '../../core/dispatch-card';
import type { DispatchCard, DispatchCardCrew } from '../../core/dispatch-card';
import { PERSONAL_ID_PREFIX, personalIdLabel } from '../../core/dispatch-report';
import { EXTERNAL_PERSON_TAG, TEMPORARY_VEHICLE_LABEL } from '../../types/assignment';

interface DispatchOrderCardProps {
  card: DispatchCard;
  /** The share row the board and the detail screens hang underneath the card. */
  actions?: ReactNode;
}

/**
 * The one metadata chip of the header band — a status, a task type, a convoy
 * size. Set on the band's own dark ground rather than in a coloured pill, so
 * the header reads as one object instead of a row of badges.
 */
function HeaderChip({ children }: { children: ReactNode }) {
  return (
    <span className="rounded-lg border border-white/15 bg-white/10 px-2.5 py-1 text-[11px] font-semibold tracking-wide text-slate-100">
      {children}
    </span>
  );
}

/** One block of the order — a heading and whatever the block holds. */
function Block({ title, icon, children }: { title: string; icon: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm p-3 text-right">
      <h4 className="text-[11px] font-bold uppercase tracking-wide text-slate-500 flex items-center gap-1.5 mb-2 border-b border-slate-200 pb-1.5">
        <span className="text-slate-400" aria-hidden>
          {icon}
        </span>
        {title}
      </h4>
      {children}
    </section>
  );
}

/** A label/value row. Values wrap — nothing on this card scrolls sideways. */
function Row({ label, value, ltr }: { label: string; value: string; ltr?: boolean }) {
  return (
    <p className="text-sm leading-6 break-words">
      <span className="text-slate-500">{label}: </span>
      <span
        className={ltr ? 'font-bold text-slate-900 tabular-nums' : 'font-bold text-slate-900'}
        data-dir={ltr ? 'ltr' : undefined}
      >
        {value || DISPATCH_CARD_EMPTY}
      </span>
    </p>
  );
}

/**
 * One rider of a vehicle. Only assigned seats reach here, so a crew line is
 * always a person — never a role with a dash after it.
 *
 * Two tiers, and the split is the whole point. `נהג: בן פרץ` is the line — bold,
 * near-black, the thing a driver finds when they glance at the order. Under it,
 * quietly, the numbers: the phone first as a quick-action tag, because that is
 * the one a manager taps, and the מ.א. after it in the smallest, lightest type
 * on the card. The personal number is still on every reading of the order, as
 * the unit requires — it simply no longer elbows the name off its own line.
 */
function CrewLine({ member }: { member: DispatchCardCrew }) {
  // The prefix is the engine's to spell — a screen that re-types it will drift.
  const personalId = personalIdLabel(member.personalNumber);
  const details = joinParts([
    member.jobLabel,
    member.licenseLabel ? `דרגת רישיון: ${member.licenseLabel}` : '',
  ]);
  // The mark leads the name line for somebody off the roster, and is built here
  // so the line itself stays what it has always been: the name, in bold.
  const mark = member.external ? (
    <span className="ml-1.5 inline-block rounded-md bg-violet-700 px-1.5 py-0.5 align-middle text-[11px] font-bold text-white">
      {EXTERNAL_PERSON_TAG}
    </span>
  ) : null;
  return (
    <li
      className={[
        'border-t border-slate-200 pt-2',
        // An off-roster rider is set on its own tinted block, exactly as the
        // shared image draws them — one glance says who is not on the מצבה.
        member.external ? 'mt-1.5 rounded-lg border border-violet-300 bg-violet-50 p-2' : '',
      ].join(' ')}
    >
      <p className="text-sm font-bold text-slate-900 break-words leading-6">
        {mark}
        <span className="text-slate-500 font-semibold">{member.roleLabel}: </span>
        {member.name}
      </p>

      {member.phone || personalId ? (
        <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 leading-5">
          {member.phone ? (
            member.phoneHref ? (
              <a
                href={member.phoneHref}
                data-dir="ltr"
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-sm font-bold tabular-nums text-slate-900"
              >
                <Phone size={13} className="text-slate-400" aria-hidden />
                {member.phone}
              </a>
            ) : (
              <span data-dir="ltr" className="text-sm font-bold tabular-nums text-slate-900">
                {member.phone}
              </span>
            )
          ) : null}
          {personalId ? (
            <span className="text-slate-500 text-xs font-normal" title="מספר אישי">
              {`${PERSONAL_ID_PREFIX} `}
              <span data-dir="ltr" className="tabular-nums">
                {member.personalNumber}
              </span>
            </span>
          ) : null}
        </p>
      ) : null}

      {details ? <p className="mt-0.5 text-xs text-slate-500 break-words">{details}</p> : null}

      {/* The vehicle they arrived in — never a fleet record, so it is labelled
          for what it is and its plate is set as the same licence tag. */}
      {member.temporaryVehicleModel || member.temporaryPlateNumber ? (
        <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
          <span className="font-bold text-violet-800">
            {joinParts([TEMPORARY_VEHICLE_LABEL, member.temporaryVehicleModel], ': ')}
          </span>
          {member.temporaryPlateNumber ? (
            <span
              data-dir="ltr"
              className="inline-block rounded-lg bg-slate-900 px-2.5 py-0.5 font-mono text-sm font-bold tracking-wide text-white"
            >
              {member.temporaryPlateNumber}
            </span>
          ) : null}
        </p>
      ) : null}
    </li>
  );
}

/**
 * פקודת משימה — תצוגה לאורך.
 *
 * The dispatch order of one task as a single vertical column: the blocks a
 * written order carries, one under the other, sized for a phone held upright.
 * There is no table and nothing that scrolls sideways anywhere in it — that is
 * the whole point of the card, and why the wide sheet is not simply reused on a
 * phone.
 *
 * A high-end operational register throughout: a tactical navy command band
 * closed by a metallic rule, white panels ruled in slate over a quiet ground,
 * and no urgency tag — the order states the mission, and the board is where a
 * task is ranked. The two things a driver checks in a lit yard at night, the
 * internal number and the plate, are the highest-contrast objects on the card:
 * one boxed in near-black, one set white on near-black. Each vehicle carries its
 * own crew, and only the seats that were filled, so an unassigned escort
 * produces no row rather than an empty one.
 *
 * Presentational only: every value is built by `src/core/dispatch-card.ts`, the
 * same model the shared image is drawn from, so the screen and the PNG can
 * never disagree about what the order says — nor, now, about how it looks.
 */
export function DispatchOrderCard({ card, actions }: DispatchOrderCardProps) {
  return (
    <article
      data-dispatch-card={card.taskId}
      className="print-card mx-auto w-full min-w-0 max-w-md box-border rounded-2xl border border-slate-200 bg-slate-50 shadow-sm overflow-hidden text-right"
    >
      {/* ── The command band: what, of which kind, and how large ── */}
      <header className="bg-[#0F172A] text-white p-4 space-y-2.5 border-b-4 border-slate-400/60">
        <p className="text-[11px] font-semibold tracking-[0.12em] text-slate-400 flex items-center gap-1.5">
          <ClipboardList size={13} aria-hidden />
          {DISPATCH_CARD_TITLE}
        </p>
        <h3 className="text-xl font-black leading-tight break-words text-white">{card.title}</h3>
        <div className="flex flex-wrap items-center gap-1.5">
          <HeaderChip>{card.statusLabel}</HeaderChip>
          {card.typeName ? <HeaderChip>{card.typeName}</HeaderChip> : null}
          <HeaderChip>
            <span data-dir="ltr" className="tabular-nums">
              {card.vehicleCount}
            </span>
            {' רכבים · '}
            <span data-dir="ltr" className="tabular-nums">
              {card.crewCount}
            </span>
            {' אנשי צוות'}
          </HeaderChip>
        </div>
      </header>

      <div className="p-3 space-y-2.5">
        <Block title={DISPATCH_CARD_SECTION_TITLES.times} icon={<CalendarClock size={14} />}>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-2 min-w-0">
              <p className="text-[11px] text-slate-500">תאריך</p>
              <p className="text-base font-bold text-slate-900 break-words">
                {card.dateLabel || DISPATCH_CARD_EMPTY}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-2 min-w-0">
              <p className="text-[11px] text-slate-500">שעת התחלה וסיום</p>
              <p data-dir="ltr" className="text-base font-bold text-slate-900 tabular-nums">
                {card.window || DISPATCH_CARD_EMPTY}
              </p>
            </div>
          </div>
        </Block>

        <Block title={DISPATCH_CARD_SECTION_TITLES.destination} icon={<MapPin size={14} />}>
          <Row label="יעד" value={card.destination} />
          {card.routeHref ? (
            <a
              href={card.routeHref}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1.5 inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-300 bg-slate-100 px-3 text-sm font-semibold text-slate-800"
            >
              <MapPin size={14} aria-hidden />
              קו ניווט
            </a>
          ) : null}
          <div className="mt-1.5">
            <Row label="איש קשר" value={joinParts([card.contactName, card.contactRole])} />
            {card.contactPhoneHref ? (
              <a
                href={card.contactPhoneHref}
                data-dir="ltr"
                className="mt-1 inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm font-bold tabular-nums text-slate-900"
              >
                <Phone size={14} className="text-slate-400" aria-hidden />
                {card.contactPhone}
              </a>
            ) : null}
          </div>
        </Block>

        <Block
          title={`${DISPATCH_CARD_SECTION_TITLES.vehicles} (${card.vehicleCount}) · ${DISPATCH_CARD_SECTION_TITLES.crew}`}
          icon={<Truck size={14} />}
        >
          {card.vehicles.length === 0 ? (
            <p className="text-sm text-slate-500">טרם שובצו רכבים למשימה.</p>
          ) : (
            <ul className="space-y-2">
              {card.vehicles.map((vehicle) => (
                <li
                  key={vehicle.teamId}
                  className="rounded-xl border border-slate-200 bg-white shadow-sm p-2.5 min-w-0"
                >
                  <div className="flex items-start gap-2.5 min-w-0">
                    {/* The internal number: boxed in near-black, the largest
                        thing on the line, exactly as the image draws it. */}
                    <span
                      data-dir="ltr"
                      title="מספר צ / מספר פנימי"
                      className="shrink-0 rounded-lg border-2 border-slate-900 bg-white px-2.5 py-1 text-xl font-black tabular-nums text-slate-900"
                    >
                      {vehicle.callSign}
                    </span>
                    <div className="min-w-0">
                      <p className="font-bold text-slate-900 break-words">
                        {joinParts([vehicle.model || vehicle.name, vehicle.typeLabel])}
                      </p>
                      {/* The plate as a licence tag — white on near-black,
                          monospaced, the one object read at a distance. */}
                      {vehicle.plateNumber ? (
                        <span
                          data-dir="ltr"
                          className="mt-1 inline-block font-mono font-bold bg-slate-900 text-white px-3 py-1 rounded-lg text-sm tracking-wide"
                        >
                          {vehicle.plateNumber}
                        </span>
                      ) : null}
                      <p className="mt-1 text-xs text-slate-500 break-words">
                        {vehicle.company || DISPATCH_CARD_EMPTY}
                      </p>
                    </div>
                  </div>
                  {/* Only the seats that were filled — no empty role rows. */}
                  {vehicle.crew.length > 0 ? (
                    <ul className="mt-2 space-y-1.5">
                      {vehicle.crew.map((member, index) => (
                        <CrewLine key={`${member.teamId}-${member.roleLabel}-${index}`} member={member} />
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-2 border-t border-slate-200 pt-1.5 text-xs text-slate-500">
                      טרם שובץ צוות לרכב זה.
                    </p>
                  )}
                  {vehicle.note ? (
                    <p className="mt-1.5 text-xs text-slate-500 break-words">
                      הערת צוות: {vehicle.note}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </Block>

        {card.notes ? (
          <Block title={DISPATCH_CARD_SECTION_TITLES.notes} icon={<StickyNote size={14} />}>
            <p className="rounded-lg border border-slate-200 border-r-4 border-r-slate-900 bg-slate-50 p-2.5 text-sm text-slate-900 break-words">
              {card.notes}
            </p>
          </Block>
        ) : null}

        {actions ? <div data-print="hide" className="pt-1">{actions}</div> : null}
      </div>
    </article>
  );
}
